import { toRaw } from 'vue';
import { createGraph } from '../graph/graphModel.js';
import { migrateHassConfigs } from './hassMigration.js';
import { pageHassObjects } from './pageHass.js';

const FLOW_LISTS = ['automation', 'properties', 'events'];

/** Whether a hass_config holds anything the flow graph takes over. */
export function hasHassFlowConfig(config) {
    return Boolean(config) && (Boolean(String(config.primaryEntity ?? '').trim()) ||
        FLOW_LISTS.some(key => Array.isArray(config[key]) && config[key].length > 0));
}

/**
 * Move the HASSBuilder configuration of a page's objects (`hass_config`)
 * into the page's Home Assistant flow graph, then strip it from the objects
 * (see migrateHassConfigs). Idempotent: objects without such configuration
 * are skipped.
 *
 * Kept apart from pageHass.js so HaspPage does not import the migration
 * (and through it the automation AST and legacy converters).
 *
 * @returns {boolean} whether anything was migrated
 */
export function migratePageHass(page, { plateWidth = 0 } = {}) {
    page = toRaw(page);
    const objects = pageHassObjects(page).filter(node => hasHassFlowConfig(node.hass_config));
    if (objects.length === 0) return false;
    const toPage = page.getAbsoluteTransform?.().copy().invert();
    const { graph, remaining } = migrateHassConfigs(objects.map(node => ({
        uuid: node.id(),
        object: node,
        hassConfig: node.hass_config,
        y: toPage ? toPage.point(node.getAbsolutePosition()).y : (node.y?.() ?? 0),
    })), { graph: page.hass ?? createGraph(), pageId: page.haspid, plateWidth });
    page.hass = graph;
    for (const node of objects) {
        const rest = remaining.get(node.id()) ?? {};
        node.hass_config = Object.keys(rest).length > 0 ? rest : undefined;
    }
    return true;
}

export function migrateLayerHass(layer) {
    const raw = toRaw(layer);
    return (raw?.getChildren?.() ?? [])
        .filter(page => page?.obj === 'page' || page?.obj === 'template_page')
        .map(page => migratePageHass(page, { plateWidth: raw.pageWidth ?? 0 }))
        .some(Boolean);
}
